import React from 'react'
import { BoundActionCreator } from '@sendoutcards/core'
// @src imports
import connect from 'src/redux/connect'
import { Category as ItemCategory, Item } from 'src/campaign_store/types'
import { setAppMessage } from 'src/redux/actions/messages'
import { openPayment } from 'src/redux/actions/payments'
import { openCampaignStore } from 'src/redux/actions/routeAliases'
import { performOperation } from 'src/app/api'
import { ACCOUNT_TOOLS_ID } from 'src/app/constants'
import { PurchaseTypes } from 'src/payments/containers/Payment'
import { ProductPreviewModal } from 'src/campaign_store/components'
import { PersistState } from 'src/chrome'
import { PricingRoute } from 'src/marketing/plans/routes/PricingRoute'
import { Category, GetCategory, PlanFragment } from 'src/graphql'
import { PlansState } from 'src/redux/reducers/plans'
import { getPlan } from 'src/helpers'
import {
  mobilePricingTableData,
  pricingTableData,
} from 'src/redux/selectors/plans'
// relative imports
import { Plan } from '../types'
import {
  AccountStorage,
  AutomatedSendsGrid,
  BasicPlan,
  MobilePricingTable,
  PlanContentsGrid,
  PricingTable,
  WaysToSend,
} from '../components'

import styles from './plans.module.scss'
import { openApp } from 'src/helpers/appHelpers'

type MessagesActions = {
  setAppMessage: BoundActionCreator<typeof setAppMessage>
}

type PaymentsActions = {
  openPayment: BoundActionCreator<typeof openPayment>
}

type RoutesActions = {
  openCampaignStore?: () => void
}

type PlansProps = {
  upsale?: {
    title?: string
    message?: string
    highlightPlan?: PlanFragment
  }
  pricingInfo: Category[]
  mobilePricingInfo: Plan[]
  route?: PricingRoute
  screenWidth?: number
} & PlansState &
  MessagesActions &
  PaymentsActions &
  RoutesActions

type State = {
  accountTools?: ItemCategory | null
  activeItem?: Item | null
  upgradeConfirm: boolean
  cancelConfirmation: boolean
  downgrade?: string
  selectedPlan?: Plan
  selectedItem: Item | null
  purchasing?: boolean
  isAffiliate: boolean
  join: boolean
  next: string | null
}

const initialState: State = {
  upgradeConfirm: false,
  cancelConfirmation: false,
  selectedItem: null,
  purchasing: false,
  isAffiliate: false,
  join: false,
  next: null,
}

class Plans extends React.Component<PlansProps, State> {
  state: State = {
    ...initialState,
  }

  componentDidMount() {
    this.loadAccountTools()
  }

  handleAffiliate = () => {
    openApp('pricing', { join: '', affiliate: '' })
  }

  onCloseModal = () => {
    this.setState({
      ...initialState,
      next: this.state.next,
    })
  }

  onFinishJoin = () => {
    const { next, selectedPlan } = this.state

    this.setState({
      join: false,
    })

    if (next) {
      if (
        next === 'showPlanUpgrade' &&
        selectedPlan &&
        selectedPlan.title === 'Basic'
      ) {
        window.location.reload(false)
      } else {
        ;(this as any)[next]()
      }
    }
  }

  closeCancel = () => {
    this.setState({
      cancelConfirmation: false,
    })
  }

  showPlanUpgrade = (planTitle: string) => {
    const { plans } = this.props
    const { selectedPlan } = this.state

    const plan: any =
      (plans && plans.find(p => p.title === planTitle)) || selectedPlan
    // need to modify promo query param to be plan id
    openApp('', { join: '', promo: plan.id })
  }

  loadAccountTools = () =>
    performOperation(GetCategory({ id: ACCOUNT_TOOLS_ID }), result =>
      result.select('itemCategory').match(
        itemCategory =>
          this.setState({
            accountTools: {
              ...itemCategory,
              children: itemCategory.children.map(child => ({
                ...child,
                children: [],
              })),
            },
          }),
        error => console.log(error),
      ),
    )

  purchase = (item: any) => {
    this.props?.openPayment({
      items: [item],
      purchaseType: PurchaseTypes.ITEMS,
    })
  }

  closeProductPreview = () => {
    this.setState({ activeItem: null })
  }

  handleAddonPurchaseClick = () => {
    openApp()
  }

  componentWillUnmount() {
    window.localStorage.removeItem('join-page')
  }

  getPlan = (planId: string): PlanFragment | undefined =>
    getPlan(this.props.plans, planId)

  // tslint:disable-next-line: cyclomatic-complexity score 39
  render() {
    const { plans, pricingInfo, upsale } = this.props
    const upsaleTitle = upsale?.title
    const upsaleMessage = upsale?.message
    const defaultUpsale =
      'We have the perfect subscription for everyone, no matter how you choose to use SendOutCards. Start making a difference by choosing the plan that will work best for you. If you change your mind later, no worries — you can always switch plans or cancel.'
    return (
      <div>
        <div
          className={styles.plansContainer}
          style={
            !upsaleTitle || !upsaleMessage
              ? {
                  marginTop: 50,
                }
              : {}
          }
        >
          <PersistState
            name="join-page"
            data={this.state}
            onMount={data => {
              this.setState({
                ...data,
                purchasing: false,
                showResponseModal: false,
                upgradeConfirm: false,

                join: false,
              })
            }}
          />
          <div className={styles.planCopy}>
            <h4>{upsaleTitle ?? 'Upgrade your life today.'}</h4>
            <p>{upsaleMessage ?? defaultUpsale}</p>
          </div>
          <div className={styles.pricingTable}>
            <div
              className={`${styles.pricingTableContainer} ${styles.desktopTable}`}
            >
              {pricingInfo && (
                <PricingTable
                  account={undefined}
                  categories={pricingInfo}
                  plans={plans || []}
                  currentPlanId={''}
                  stripeSubscription={undefined}
                  onSelect={this.showPlanUpgrade}
                  getPlan={this.getPlan}
                />
              )}
            </div>
            <div
              className={`${styles.pricingTableContainer} ${styles.mobileTable}`}
            >
              {this.props.mobilePricingInfo && (
                <MobilePricingTable
                  account={undefined}
                  plans={this.props.mobilePricingInfo}
                  currentPlan={''}
                  onSelect={this.showPlanUpgrade}
                  stripeSubscription={undefined}
                  getPlan={this.getPlan}
                />
              )}
            </div>
          </div>
          <BasicPlan onSelect={this.showPlanUpgrade} />
          <WaysToSend screenWidth={this.props.screenWidth} />
          <PlanContentsGrid />
          <AutomatedSendsGrid />
          <AccountStorage screenWidth={this.props.screenWidth} />
          {this.state.activeItem && (
            <ProductPreviewModal
              product={this.state.activeItem}
              onClose={this.closeProductPreview}
              onPurchase={this.purchase}
            />
          )}
        </div>
      </div>
    )
  }
}

export default connect(
  state => {
    const pricingInfo = pricingTableData(
      state.plans.categories,
      state.plans.features,
      state.plans.plans,
    )
    const mobilePricingInfo = mobilePricingTableData(
      state.plans.categories,
      state.plans.features,
      state.plans.plans,
    )
    return {
      ...state.plans,
      pricingInfo,
      mobilePricingInfo,
      upsale:
        state.plans.upsaleMessage &&
        state.plans.upsaleTitle &&
        state.plans.highlightPlan
          ? {
              message: state.plans.upsaleMessage,
              title: state.plans.upsaleTitle,
              highlightPlan: state.plans.highlightPlan,
            }
          : undefined,
      screenWidth: state.window.width,
    }
  },
  {
    setAppMessage,
    openPayment,
    openCampaignStore,
  },
)(Plans)
